import React, { useState } from "react"
import PropTypes from "prop-types"
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
  Stack,
  Checkbox,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import i18n from "i18n"

const PartidaAccordion = ({
  partida,
  selectedItems = {},
  onSelect,
  parentChapters = [],
}) => {
  const [expanded, setExpanded] = useState(false)
  const hasDecomposition =
    Array.isArray(partida.decomposition) && partida.decomposition.length > 0

  // Handler para click en chip de descomposición
  const handleChipClick = (idx, e) => {
    e.stopPropagation()
    if (onSelect) {
      onSelect(
        "decomposition",
        { partidaId: partida.partidaId, decompIdx: idx },
        parentChapters,
      )
    }
  }

  if (!hasDecomposition) {
    const checked = !!selectedItems[partida.partidaId]
    const handleCheckboxClick = e => {
      e.stopPropagation()
      if (onSelect) {
        onSelect("partida", partida, parentChapters)
      }
    }
    return (
      <Box
        sx={{
          ml: 2,
          my: 0.5,
          p: 1,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Checkbox
          size="small"
          sx={{ p: 0, mr: 1 }}
          checked={checked}
          onClick={handleCheckboxClick}
          onFocus={e => e.stopPropagation()}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: "medium",
            fontSize: "0.9rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {partida.name}
        </Typography>
      </Box>
    )
  }

  // Indeterminate logic for partidas with decompositions
  let checked = !!selectedItems[partida.partidaId]
  let indeterminate = false
  if (hasDecomposition) {
    const decompIds = partida.decomposition.map(
      (_, idx) => `${partida.partidaId}_${idx}`,
    )
    const checkedCount = decompIds.filter(id => !!selectedItems[id]).length

    checked =
      !!selectedItems[partida.partidaId] ||
      (checkedCount === decompIds.length && decompIds.length > 0)
    indeterminate = checkedCount > 0 && checkedCount < decompIds.length
  }
  const handleCheckboxClick = e => {
    e.stopPropagation()
    if (onSelect) {
      onSelect("partida", partida, parentChapters)
    }
  }
  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(prev => !prev)}
      sx={{
        mb: 0.5,
        boxShadow: "none",
        border: "1px solid",
        borderColor: "divider",
        ml: 2,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel-partida-${partida.partidaId}-content`}
        id={`panel-partida-${partida.partidaId}-header`}
        sx={{
          pl: 1,
          minHeight: "38px !important",
          height: "38px",
          "&.Mui-expanded": {
            minHeight: "38px !important",
            height: "38px",
            marginBottom: "2px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            minWidth: 0,
          }}
        >
          <Checkbox
            size="small"
            sx={{ p: 0, mr: 1 }}
            checked={checked}
            indeterminate={indeterminate}
            onClick={handleCheckboxClick}
            onFocus={e => e.stopPropagation()}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: "medium",
              fontSize: "0.9rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "180px",
            }}
          >
            {partida.name}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 1, pt: 0.5 }}>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {partida.decomposition.map((item, idx) => {
            const decompId = `${partida.partidaId}_${idx}`
            const selected = !!selectedItems[decompId]
            return (
              <Chip
                key={idx}
                label={
                  item.description ||
                  `${i18n.t("costs.modal.element")} ${idx + 1}`
                }
                size="small"
                clickable
                onClick={e => handleChipClick(idx, e)}
                sx={{
                  backgroundColor: selected ? "#c2e2f1" : "divider",
                  color: selected ? "primary.main" : "divider.main",
                  "&:hover": selected
                    ? { backgroundColor: "#c2e2f1" }
                    : { backgroundColor: "grey.200" },
                  "&.MuiChip-clicked, &:active": selected
                    ? { backgroundColor: "#c2e2f1" }
                    : {},
                }}
              />
            )
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

PartidaAccordion.propTypes = {
  partida: PropTypes.shape({
    partidaId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    decomposition: PropTypes.array,
    decompositions: PropTypes.array,
  }).isRequired,
  selectedItems: PropTypes.object,
  onSelect: PropTypes.func,
  parentChapters: PropTypes.array,
}

export default PartidaAccordion
